import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { getBlogPosts } from '../lib/api'
import GlassCard from '../components/ui/GlassCard'
import ScrollReveal from '../components/ui/ScrollReveal'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import Parallax from '../components/ui/Parallax'
import type { BlogPost } from '../types'

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getBlogPosts().then((res) => {
      setPosts(res)
      setLoading(false)
    })
  }, [])

  return (
    <main>
      {/* Page Header */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gray-950" />
        <Parallax speed={0.08} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px]">
          <div className="w-full h-full rounded-full bg-premium-500/10 blur-[120px]" />
        </Parallax>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-4 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-electric-blue bg-electric-blue/10 rounded-full border border-electric-blue/20"
          >
            Our Blog
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white"
          >
            Insights &{' '}
            <span className="bg-gradient-to-r from-electric-blue to-electric-purple bg-clip-text text-transparent">
              Stories
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-lg text-gray-400 max-w-xl mx-auto"
          >
            Thoughts on design, development, and digital strategy from the SST
            team.
          </motion.p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <LoadingSpinner text="Loading articles..." />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, i) => (
                <ScrollReveal key={post.id} delay={i * 0.1}>
                  <GlassCard className="overflow-hidden group cursor-pointer h-full">
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/20 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 text-xs font-medium text-white bg-electric-blue/80 rounded-full backdrop-blur-sm">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                        <span>{post.author}</span>
                        <span>&middot;</span>
                        <span>
                          {new Date(post.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </span>
                      </div>

                      <h3 className="text-lg font-bold text-white group-hover:text-electric-blue transition-colors duration-300 mb-3">
                        {post.title}
                      </h3>

                      <p className="text-sm text-gray-400 leading-relaxed flex-1">
                        {post.excerpt}
                      </p>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {post.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2.5 py-1 text-xs text-gray-500 bg-white/5 rounded-md"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>

                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          window.open('#', '_blank')
                        }}
                        className="group/read mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-electric-blue hover:text-white transition-colors shrink-0 w-fit"
                      >
                        <span className="relative">
                          Read More
                          <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-gradient-to-r from-electric-blue to-electric-purple group-hover/read:w-full transition-all duration-300" />
                        </span>
                        <svg className="w-3.5 h-3.5 group-hover/read:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </a>
                    </div>
                  </GlassCard>
                </ScrollReveal>
              ))}
            </div>
          )}

          {/* Placeholder for future CMS content */}
          {!loading && posts.length === 0 && (
            <div className="text-center py-20">
              <div className="text-4xl mb-4">📝</div>
              <h3 className="text-xl text-white font-semibold mb-2">
                Coming Soon
              </h3>
              <p className="text-gray-400">
                We're crafting our first articles. Check back soon!
              </p>
            </div>
          )}
        </div>
      </section>
    </main>
  )
}
